import { SentryRoutes } from '@vrplatform/data';
import {
  AuthenticateWithRedirectCallback,
  ClerkLoaded,
  ClerkLoading,
  ClerkProvider,
  OAuthConsent,
  Show,
  useUser,
} from '@clerk/react';
import { Button, LoadingIndicator } from '@vrplatform/components';
import {
  getPortalSwitchMessage,
  type OwnerPortalPreview,
  OwnerPortalPreviewOpen,
  OwnerPortalPreviewSessionProvider,
  readOwnerPortalPreview,
  readPortalSwitchLanding,
  TrpcProvider,
  usesOwnerPortalBootstrap,
  useOwnerPortalBootstrap,
  ownerPortalContext,
  useOwnerPortal,
  useTrackingPage,
  useTrackingReset,
  TanstackQueryClient,
  TanstackQueryClientProvider,
} from '@vrplatform/data';
import { Box, Loader, Text } from '@mantine/core';
import { useInterval } from '@mantine/hooks';
import React, { useEffect } from 'react';
import { useState } from 'react';
import { Outlet, Route, useLocation, useNavigate } from 'react-router';
import { EmptyViewWrapper, FullPageLoadingIndicator } from '../components';
import { SpotlightComponentProvider } from '../components/spotlight';
import { getClerkPublishableKey } from './consts';
import { AppRoutes } from '../routes';
import { lazyWithRetry } from '../routes/dashboard/_utils';
import { VerifyTokenView } from '../views/invite-redirect/VerifyTokenView';
import {
  OwnerPortalSignIn,
  getOwnerPortalClerkLocalization,
} from '../views/owner-portal/OwnerPortalSignIn';
import { OwnerPortalTransition } from '../views/owner-portal/OwnerPortalTransition';
import { DBProvider } from './DBProvider';
import { OwnerPortalPreviewEntry } from './OwnerPortalPreview';
import { QueryDevTools } from './QueryDevTools';
import { OperationTrackerProvider } from '../operations';
import { SetupLoadingScreen } from '../views/onboarding/components/SetupLoadingScreen';

const FailedInviteView = lazyWithRetry(
  () => import('../views/invite-redirect/FailedInvite')
);
const InvitationRedirect = lazyWithRetry(
  () => import('../views/invite-redirect/InvitationRedirect')
);
const SignInRoutes = lazyWithRetry(
  () => import('../views/onboarding/views/sign-in-up/SignInRoutes')
);
const StatementLinkView = lazyWithRetry(() =>
  import('../views/statement-link/StatementLinkView').then((module) => ({
    default: module.StatementLinkView,
  }))
);
const BankDetailsLinkView = lazyWithRetry(() =>
  import('../views/bank-details-link/BankDetailsLinkView').then((module) => ({
    default: module.BankDetailsLinkView,
  }))
);
const StatementUnsubscribeView = lazyWithRetry(() =>
  import('../views/statement-link/StatementUnsubscribeView').then((module) => ({
    default: module.StatementUnsubscribeView,
  }))
);

const queryClient = new TanstackQueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

export const Auth = () => {
  const location = useLocation();
  const isSatellite = usesOwnerPortalBootstrap(window.location.hostname);
  const requestedHostname = isSatellite
    ? window.location.hostname
    : new URLSearchParams(location.search).get('owner_portal')?.toLowerCase();
  const isPrimaryLogin =
    ['/owner-login', '/owner-sign-up'].includes(location.pathname) ||
    (!!requestedHostname &&
      ['/invitation', '/token-verify'].includes(location.pathname));
  // Nothing below mounts Clerk or the API client until the registration has
  // validated; an unknown hostname therefore never reaches production.
  const { portal, failed } = useOwnerPortalBootstrap(
    requestedHostname,
    isSatellite || isPrimaryLogin
  );

  if (failed && (isSatellite || isPrimaryLogin))
    return (
      <EmptyViewWrapper
        actions={
          <Button onClick={() => window.location.reload()}>Try again</Button>
        }
        title="Owner portal is unavailable"
        description="Please try again later or contact your property manager."
      />
    );
  if (
    (isSatellite || isPrimaryLogin) &&
    (!portal || portal.hostname !== requestedHostname)
  )
    return <FullPageLoadingIndicator showSignOut={false} />;

  const activePortal = isSatellite || isPrimaryLogin ? portal : null;
  return (
    <ownerPortalContext.Provider value={activePortal}>
      {activePortal && (
        // React places this in the document head, so the browser tab shows
        // the portal's name instead of the platform's. The portal edge
        // replaces the icons in the served HTML.
        <title>{activePortal.name}</title>
      )}
      <AuthContent />
    </ownerPortalContext.Provider>
  );
};

const AuthContent = () => {
  const navigate = useNavigate();
  const portal = useOwnerPortal();
  const isSatellite = usesOwnerPortalBootstrap(window.location.hostname);
  const location = useLocation();
  const ownerPortalPreview = readOwnerPortalPreview();

  const page = useTrackingPage();

  // biome-ignore lint/correctness/useExhaustiveDependencies: location pathname is not a dependency, but a trigger for tracking page
  useEffect(() => {
    page();
  }, [page, location.pathname]);

  // Birdie recording links must keep their query params and avoid Clerk auth
  // redirects, so expose a blank recorder page before Clerk handles routing.
  if (location.pathname.replace(/\/+$/, '') === '/recorder') {
    return null;
  }

  return (
    <ClerkProvider
      publishableKey={getClerkPublishableKey()}
      isSatellite={isSatellite}
      domain={isSatellite ? portal?.hostname : undefined}
      signInUrl={isSatellite ? portal?.signInUrl : undefined}
      signUpUrl={isSatellite ? portal?.signUpUrl : undefined}
      localization={
        portal ? getOwnerPortalClerkLocalization(portal.name) : undefined
      }
      // Undefined keeps Clerk's default (unrestricted) behaviour on the
      // platform host; a list would turn existing cross-origin returns unsafe.
      allowedRedirectOrigins={
        portal ? [`https://${portal.hostname}`] : undefined
      }
      routerPush={(to: string) => {
        const target = new URL(to, window.location.href);
        if (target.origin === window.location.origin)
          navigate(`${target.pathname}${target.search}${target.hash}`);
        else window.location.assign(target.href);
      }}
      routerReplace={(to: string) => {
        const target = new URL(to, window.location.href);
        if (target.origin === window.location.origin)
          navigate(`${target.pathname}${target.search}${target.hash}`, {
            replace: true,
          });
        else window.location.replace(target.href);
      }}
    >
      {/* Emailed statement links open without a login on every host. The
          page exchanges its link while Clerk loads and waits for Clerk only
          to send a signed-in owner to the portal. */}
      {location.pathname === '/statement-link' && !ownerPortalPreview ? (
        <React.Suspense fallback={<AuthLoading />}>
          <StatementLinkView />
        </React.Suspense>
      ) : location.pathname === '/bank-details' ? (
        // Emailed vendor bank details forms never need a login.
        <React.Suspense fallback={<AuthLoading />}>
          <BankDetailsLinkView />
        </React.Suspense>
      ) : (
        <>
          <ClerkLoading>
            <AuthLoading />
          </ClerkLoading>

          <ClerkLoaded>
            {/* Owner portal previews and statement unsubscribe pages open
                without a login on every host. */}
            {location.pathname === '/owner-preview/open' ? (
              <OwnerPortalPreviewOpen
                renderStatus={(status) =>
                  status.status === 'loading' ? (
                    <FullPageLoadingIndicator
                      message="Starting owner portal preview..."
                      showSignOut={false}
                    />
                  ) : (
                    <EmptyViewWrapper
                      title="Owner portal preview unavailable"
                      description="This preview link was already used or has expired. Open the preview again from the owner's menu."
                      actions={
                        <Button onClick={() => window.close()}>
                          Close tab
                        </Button>
                      }
                    />
                  )
                }
              />
            ) : ownerPortalPreview ? (
              <OwnerPortalPreviewRoutes preview={ownerPortalPreview} />
            ) : location.pathname === '/statement-unsubscribe' ? (
              <React.Suspense fallback={<AuthLoading />}>
                <StatementUnsubscribeView />
              </React.Suspense>
            ) : ['/owner-login', '/owner-sign-up'].includes(
                location.pathname
              ) && !isSatellite ? (
              <OwnerPortalSignIn />
            ) : (
              <>
                <Show when="signed-in">
                  {portal && location.pathname === '/invitation' ? (
                    <OwnerPortalTransition />
                  ) : (
                    <AuthNewUserDelay>
                      {portal &&
                      !isSatellite &&
                      location.pathname === '/token-verify' ? (
                        <OwnerPortalTransition />
                      ) : (
                        <SignedInRoutes />
                      )}
                    </AuthNewUserDelay>
                  )}
                </Show>
                <Show when="signed-out">
                  {isSatellite ? <OwnerPortalSignIn /> : <SignOutRoutes />}
                </Show>
              </>
            )}
          </ClerkLoaded>
        </>
      )}
    </ClerkProvider>
  );
};

const SignedInRoutes = () => {
  const { user } = useUser();
  const sessionId = user?.publicMetadata?.user_id;

  if (typeof sessionId !== 'string' || !sessionId) {
    throw new Error('Signed-in routes require a linked platform user');
  }

  return (
    <SentryRoutes>
      <Route path="hello" element={<Hello />} />

      <Route path="/invite-failed" element={<FailedInviteView />} />

      <Route path="/owner-preview" element={<OwnerPortalPreviewEntry />} />

      <Route
        path="/oauth/consent"
        element={
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              minHeight: '100vh',
              width: '100%',
            }}
          >
            <OAuthConsent
              fallback={<FullPageLoadingIndicator showSignOut={false} />}
            />
          </Box>
        }
      />

      <Route
        path="/*"
        element={
          <TrpcProvider queryClient={queryClient}>
            <TanstackQueryClientProvider client={queryClient}>
              <DBProvider>
                <OperationTrackerProvider
                  queryClient={queryClient}
                  sessionId={sessionId}
                >
                  <SpotlightComponentProvider>
                    <Outlet />
                    <QueryDevTools />
                  </SpotlightComponentProvider>
                </OperationTrackerProvider>
              </DBProvider>
            </TanstackQueryClientProvider>
          </TrpcProvider>
        }
      >
        <Route path="invitation" element={<InvitationRedirect />} />
        <Route path="*" element={<AppRoutes />} />
      </Route>
    </SentryRoutes>
  );
};

/** The owner portal of a preview tab, signed in with its preview token. */
const OwnerPortalPreviewRoutes = ({
  preview,
}: {
  preview: OwnerPortalPreview;
}) => {
  return (
    <TrpcProvider queryClient={queryClient}>
      <TanstackQueryClientProvider client={queryClient}>
        <OwnerPortalPreviewSessionProvider
          preview={preview}
          renderExpired={({ restart, exit }) => (
            <EmptyViewWrapper
              title="Owner portal preview expired"
              description="A preview lasts one hour. Restart it to continue."
              actions={
                <>
                  <Button variant="primary" onClick={restart}>
                    Restart preview
                  </Button>
                  <Button onClick={exit}>Exit preview</Button>
                </>
              }
            />
          )}
        >
          <OperationTrackerProvider
            queryClient={queryClient}
            sessionId={preview.userId}
          >
            <SpotlightComponentProvider>
              <SentryRoutes>
                <Route path="*" element={<AppRoutes />} />
              </SentryRoutes>
              <QueryDevTools />
            </SpotlightComponentProvider>
          </OperationTrackerProvider>
        </OwnerPortalPreviewSessionProvider>
      </TanstackQueryClientProvider>
    </TrpcProvider>
  );
};

const SignOutRoutes = () => {
  const reset = useTrackingReset();

  useEffect(() => {
    reset(undefined);
  }, [reset]);

  return (
    <SentryRoutes>
      <Route path="token-verify" element={<VerifyTokenView />} />
      <Route path="/sso-callback" element={<SSOCallback />} />
      <Route path="/invite-failed" element={<FailedInviteView />} />
      <Route path="/invitation" element={<ValidateInvitation />} />
      <Route path="/*" element={<SignIn />} />
    </SentryRoutes>
  );
};

const SignIn = () => (
  <TrpcProvider queryClient={queryClient}>
    <TanstackQueryClientProvider client={queryClient}>
      <SignInRoutes />
    </TanstackQueryClientProvider>
  </TrpcProvider>
);

const ValidateInvitation = () => (
  <TrpcProvider queryClient={queryClient}>
    <TanstackQueryClientProvider client={queryClient}>
      <InvitationRedirect />
    </TanstackQueryClientProvider>
  </TrpcProvider>
);

function Hello() {
  const navigate = useNavigate();
  React.useEffect(() => {
    setTimeout(() => {
      navigate('/');
    }, 3000);
  });
  return (
    <Box mt={'30vh'}>
      <LoadingIndicator isFullPageLoading message="Redirecting" />
    </Box>
  );
}

// Callback for Social logins like Google etc.
function SSOCallback() {
  // Handle the redirect flow by rendering the
  // prebuilt AuthenticateWithRedirectCallback component.
  // This is the final step in the custom OAuth flow
  return <AuthenticateWithRedirectCallback />;
}

const AuthNewUserDelay = ({ children }: { children: React.ReactNode }) => {
  const { user } = useUser();
  const location = useLocation();
  const userId = user?.publicMetadata?.user_id;
  const isReady = typeof userId === 'string' && userId.length > 0;
  const [timer, setTimer] = useState<number>(0);

  const isError = !isReady && timer > 60;

  useEffect(() => {
    if (!isReady && !isError) {
      const timeout = setInterval(() => {
        setTimer((o) => o + 1);
        user?.reload();
      }, 1000);
      return () => clearInterval(timeout);
    }
  }, [isReady, isError, user?.reload]);

  if (
    !isReady &&
    (user?.unsafeMetadata?.onboardingV2 === true ||
      ['/setup', '/verify-email', '/sign-up'].includes(location.pathname))
  )
    return (
      <SetupLoadingScreen
        phase="account"
        requiresPayment={user?.unsafeMetadata?.onboardingV2 === true}
        teamType={user?.unsafeMetadata?.tenantType}
        delayed={isError ? { onRetry: () => setTimer(0) } : undefined}
      />
    );

  if (isError)
    return (
      <EmptyViewWrapper
        title="Account setup is taking longer than expected"
        description="Refresh the page to check whether setup has finished. Contact support if the issue persists."
        actions={
          <Button
            variant="primary"
            leftIcon={'RefreshCwIcon'}
            sx={{ maxWidth: 200, width: '100%', marginInline: 'auto' }}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        }
      />
    );
  else if (!isReady) {
    return (
      <Box mt={'30vh'}>
        <LoadingIndicator isFullPageLoading message="Redirecting ..." />
      </Box>
    );
  }

  return <>{children}</>;
};

const AuthLoading = () => {
  const location = useLocation();
  const portal = useOwnerPortal();
  const [timer, setTimer] = useState(0);
  const interval = useInterval(() => setTimer((s) => s + 1), 1000);

  const isError = timer > 20;

  useEffect(() => {
    interval.start();
    return interval.stop;
  }, []);

  if (location.pathname === '/setup')
    return (
      <SetupLoadingScreen
        phase="account"
        requiresPayment={false}
        delayed={
          isError ? { onRetry: () => window.location.reload() } : undefined
        }
      />
    );

  if (isError)
    return (
      <EmptyViewWrapper
        title={"We'll be right back"}
        description={
          <>
            It looks like we're having some technical issues.
            <br />
            We are working on it and will be back soon.
          </>
        }
        actions={
          <Button
            variant="primary"
            leftIcon={'RefreshCwIcon'}
            sx={{ maxWidth: 200, width: '100%', marginInline: 'auto' }}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        }
      />
    );

  if (portal)
    return (
      <EmptyViewWrapper>
        <Loader type="dots" size="md" />
        <Text c="gray" ta="center" mt="lg" size="lg">
          Loading your owner portal...
        </Text>
      </EmptyViewWrapper>
    );

  // PRO-18864: a cross-app team switch keeps its placeholder while Clerk loads.
  const portalSwitchTarget = readPortalSwitchLanding(document, sessionStorage);

  return (
    <FullPageLoadingIndicator
      message={
        portalSwitchTarget
          ? getPortalSwitchMessage(portalSwitchTarget)
          : 'Loading Dashboard...'
      }
      showSignOut={false}
    />
  );
};
